import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineSightseeingIcon from 'LuxKit/Icons/line/LineSightseeingIcon'
import TextLink from 'LuxKit/TextLink'
import AlwaysOnCheapestRateOptionMobile from 'components/OfferPage/AlwaysOn/MobileBookingFlow/AlwaysOnCheapestRateOptionMobile'
import AlwaysOnRoomDetailsModal from 'components/OfferPage/AlwaysOn/MobileBookingFlow/Modals/AlwaysOnRoomDetailsModal'
import BedbankRoomCheapestOptionRateMobile from 'components/OfferPage/Bedbank/MobileBookingFlow/BedbankRoomCheapestOptionRateMobile'
import BedbankRoomDetailsModal from 'components/OfferPage/Bedbank/MobileBookingFlow/Modals/BedbankRoomDetailsModal'
import AccommodationRoomTileDetailsVariant from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/AccommodationRoomTileDetailsVariant'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Plural from 'components/utils/Formatters/Plural'
import ModalContext from 'contexts/ModalContext'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { isHotelPackage } from 'lib/offer/offerTypes'
import { rem } from 'polished'
import React, { MouseEventHandler, useCallback, useContext, useMemo } from 'react'
import {
  arePropertyRatesLoading,
  isAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AccommodationRoomTile from 'components/AccommodationKit/AccommodationRoomTile'
import AccommodationRoomTileImage from 'components/AccommodationKit/AccommodationRoomTileImage'
import type { RateView } from '../singlePropertyUtils'

const RateSection = styled.div`
  position: relative;
  padding: ${rem(16)} ${rem(20)} ${rem(20)};
`

interface Props {
  offer: App.Offer;
  bedbankOffer?: App.BedbankOffer;
  propertyRoom: App.HotelPackage | App.BedbankPackage;
  availableRooms: number | undefined;
  displayRates: Array<RateView>;
  roomFitsCapacity: boolean;
  datesAvailable: boolean;
  checkIn?: string;
  isHighlighted?: boolean;
  roomRateUnmatched?: boolean;
  duration?: number;
  detailsModalMode: 'room-only' | 'room-and-package';
  availableForPurchase?: boolean;
  isLoading: boolean;
}

function PropertyRoomCheapestOptionMobile(props: Props) {
  const {
    offer,
    bedbankOffer,
    propertyRoom,
    availableRooms = 0,
    displayRates,
    roomFitsCapacity,
    datesAvailable,
    checkIn,
    isHighlighted,
    roomRateUnmatched,
    duration,
    detailsModalMode,
    availableForPurchase,
    isLoading,
  } = props

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao or Robert.                                                              */
  /************************************************************************************************/

  const showModal = useContext(ModalContext)
  const offerPageDispatch = useOfferPageDispatch()

  const isLpcPackage = isHotelPackage(propertyRoom)
  const opaqueRoomId = isLpcPackage ? propertyRoom.roomType.id : propertyRoom.roomId
  const roomType = isLpcPackage ? propertyRoom.roomType : undefined

  const stayPayTier = useMemo(() => {
    return displayRates.map(({ lpcRate: view }) => {
      if (view && view.hotelAvailable && view.package.stayPay) {
        return view.package.stayPay.find(tier => tier.stayNights === duration && tier.freeNights > 0)
      }
    }).find(Boolean)
  }, [displayRates, duration])

  // The first element of `displayRates` is guaranteed to either be the cheapest rate or the
  // GHA-highlighted rate (if applicable).
  const cheapestPackageView: RateView | undefined = displayRates[0]

  const hasMultipleCancellationPolicies = useMemo(() => displayRates.some(rate => rate.refundable !== displayRates[0]?.refundable), [displayRates])

  const showPartialRefundPolicyLabel = useMemo(() => hasMultipleCancellationPolicies && displayRates.some(rate => rate.bedbankRate?.rate.partiallyRefundable), [hasMultipleCancellationPolicies, displayRates])

  const handleOpenCalendar = useCallback<MouseEventHandler<HTMLAnchorElement>>(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, roomType])

  const openCapacityFilter = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, roomType])

  const onShowBedbankRoomDetails = useCallback(() => {
    const packageView = cheapestPackageView?.bedbankRate?.view
    if (packageView == null) {
      return
    }
    if (detailsModalMode === 'room-and-package') {
      // Reset LPC room type for Single Property pages
      offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
      offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomDetails' })
    } else if (isLpcPackage) {
      // Prefer the LPC room type for Single Property pages
      showModal(<AlwaysOnRoomDetailsModal room={propertyRoom.roomType} />)
    } else {
      showModal(<BedbankRoomDetailsModal roomPackage={propertyRoom} showBookmark={false} />)
    }
  }, [
    cheapestPackageView?.bedbankRate?.view,
    detailsModalMode,
    offerPageDispatch,
    isLpcPackage,
    showModal,
    propertyRoom,
  ])

  return (
    <>
      {checkIn && stayPayTier && <MessageBanner
        kind="promotional-primary"
        icon={<LineInfoCircleIcon />}
        description={<>
          <b>Special offer:</b> You get <Plural singular="night" count={stayPayTier.freeNights} withCount />{' '}
          free when staying <Plural singular="night" count={stayPayTier.stayNights} withCount /> on select packages.
        </>}
      />}
      <Pane border="neutral-five" borderRadius="S" data-testid="package-option">
        {isHighlighted && <MessageBanner
          kind="promotional-primary"
          description={<b>You were interested in this option</b>}
          icon={!roomRateUnmatched && <LineSightseeingIcon />}
        />}
        <ScrollAnchor id={opaqueRoomId} vOffset={rem(80)} />
        <AccommodationRoomTile roomPackage={propertyRoom}>
          <AccommodationRoomTileImage
            roomPackage={propertyRoom}
            // Hide the bookmark button if there's no LPC package, as otherwise we'd wind up with a
            // mix of reservation _and_ bedbank room IDs. In the future, we'd want to migrate to
            // using accommodation room IDs.
            showBookmark={isLpcPackage}
          />
          <AccommodationRoomTileDetailsVariant
            roomPackage={propertyRoom}
            urgencyLabels={<>
              {roomType && <BookingsCountLabel bookings={roomType.stats.booked} threshold="room" />}
              {checkIn && <RoomsRemainingLabel roomCount={availableRooms} threshold="rate" />}
            </>}
            actions={availableForPurchase && roomFitsCapacity && datesAvailable && roomType && <CSSBreakpoint min="desktop">
              <TripGuard>
                <BookmarkButton offer={offer} roomTypeId={roomType.id} />
              </TripGuard>
            </CSSBreakpoint>}
          />
        </AccommodationRoomTile>
        <RateSection data-testid="rate-options">
          {!isLoading && !cheapestPackageView && <>
            {!roomFitsCapacity && (
              <MessageBanner
                kind="informational-primary"
                icon={<LineExclamationCircleIcon />}
                heading="This room cannot accommodate your number of guests"
                description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={openCapacityFilter}>add more rooms</TextLink></>}
              />
            )}
            {!datesAvailable && (
              <MessageBanner
                kind="informational-primary"
                icon={<LineExclamationCircleIcon />}
                heading="This room is sold out on your dates or max number of guests exceeded"
                description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleOpenCalendar}>try different dates</TextLink></>}
              />
            )}
          </>}
          {!isLoading && cheapestPackageView?.lpcRate && <AlwaysOnCheapestRateOptionMobile
            offer={offer}
            packageView={cheapestPackageView.lpcRate}
            detailsModalMode={detailsModalMode}
            hasMultipleCancellationPolicies={hasMultipleCancellationPolicies}
            showPartialRefundPolicyLabel={showPartialRefundPolicyLabel}
          />}
          {!isLoading && cheapestPackageView?.bedbankRate && bedbankOffer && <BedbankRoomCheapestOptionRateMobile
            rate={cheapestPackageView.bedbankRate.rate}
            offer={bedbankOffer}
            packageView={cheapestPackageView.bedbankRate.view}
            hasMultipleCancellationPolicies={hasMultipleCancellationPolicies}
            showPartialRefundPolicyLabel={showPartialRefundPolicyLabel}
            onShowRoomDetails={onShowBedbankRoomDetails}
          />}
          <LoadingIndicator visible={isLoading} floating opaque />
        </RateSection>
      </Pane>
    </>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  availableForPurchase: isAvailableForPurchase(state, appState),
  isLoading: arePropertyRatesLoading(state, appState),
}), PropertyRoomCheapestOptionMobile)
